/**
 * Final composite. The only pass that runs at output resolution and the only
 * one that writes to the default framebuffer.
 *
 * Everything it needs arrives as a uniform, including the derived mask fade, so
 * the visual-critic loop can drive the entire look through `PostStack.set`
 * without a shader recompile.
 */

import * as THREE from 'three';
import { CRT_COMPOSITE_FRAG } from '../shaders/crt';
import { FullscreenPass } from './FullscreenPass';

export class CrtComposite {
  private readonly pass: FullscreenPass;
  readonly uniforms: Record<string, THREE.IUniform>;
  /** Last fit handed to `setGeometry`, kept so either input can move alone. */
  private lastScale = 1;
  private floor = 0.45;

  constructor() {
    this.uniforms = {
      tScene: { value: null },
      tPersist: { value: null },
      tBloom: { value: null },
      tHalation: { value: null },

      uOutputSize: { value: new THREE.Vector2(1, 1) },
      uBufferSize: { value: new THREE.Vector2(1, 1) },
      uTime: { value: 0 },

      uPersistMix: { value: 1 },
      uBloomIntensity: { value: 1.15 },
      uHalationStrength: { value: 0.34 },
      uHalationTint: { value: new THREE.Vector3(1.0, 0.72, 0.55) },

      uCurvature: { value: new THREE.Vector2(0.024, 0.034) },
      uOverscan: { value: 1.008 },
      uChromaticAberration: { value: 0.75 },

      uMaskPitch: { value: 3.0 },
      uMaskStrength: { value: 0.45 },
      uScanlineStrength: { value: 0.28 },
      uScanlineSharpness: { value: 2.4 },
      uScanlineMinPeriod: { value: 2.8 },
      uMaskFade: { value: 1 },
      uScanFade: { value: 1 },

      uVignette: { value: 0.85 },
      uVignettePower: { value: 1.7 },
      uVignetteDesat: { value: 0.5 },
      uEdgeBlur: { value: 1.0 },
      uEdgeBlurStart: { value: 0.62 },
      uEdgeBlurPx: { value: 2.2 },
      uNoise: { value: 0.018 },
      uGlare: { value: 0.05 },
    };

    this.pass = new FullscreenPass(CRT_COMPOSITE_FRAG, this.uniforms);
  }

  setTextures(
    scene: THREE.Texture,
    persist: THREE.Texture,
    bloom: THREE.Texture,
    halation: THREE.Texture,
  ): void {
    this.uniforms.tScene.value = scene;
    this.uniforms.tPersist.value = persist;
    this.uniforms.tBloom.value = bloom;
    this.uniforms.tHalation.value = halation;
  }

  /**
   * `scale` is output pixels per buffer pixel. Below roughly two, a three-pixel
   * grille is wider than the source pixel it is supposed to be texturing, so it
   * stops reading as phosphor structure and starts tinting the picture. That one
   * still fades out to nothing.
   *
   * The scanlines used to ride the same fade and no longer do. Their period is
   * pinned in output pixels by the shader, so they cannot alias whatever the fit
   * is; all that is left to do at a small scale is thin them, because a stripe
   * every 2.8 output pixels over a picture that is being *minified* is more
   * structure than the source carries. `scanlineFloor` is where that thinning
   * stops. Faint is a look; absent was a bug.
   */
  setGeometry(outputW: number, outputH: number, bufferW: number, bufferH: number, scale: number): void {
    (this.uniforms.uOutputSize.value as THREE.Vector2).set(outputW, outputH);
    (this.uniforms.uBufferSize.value as THREE.Vector2).set(bufferW, bufferH);
    this.lastScale = scale;
    this.applyFade();
  }

  /** Lower bound on the scanline attenuation, as a fraction. Set by `PostStack`. */
  set scanlineFloor(value: number) {
    this.floor = value;
    this.applyFade();
  }

  private applyFade(): void {
    const t = THREE.MathUtils.smoothstep(this.lastScale, 1.0, 2.5);
    this.uniforms.uMaskFade.value = t;
    this.uniforms.uScanFade.value = this.floor + (1 - this.floor) * t;
  }

  set time(value: number) {
    this.uniforms.uTime.value = value;
  }

  render(renderer: THREE.WebGLRenderer): void {
    this.pass.render(renderer, null);
  }

  dispose(): void {
    this.pass.dispose();
  }
}
